import { useState } from "react";
import type { ArrivalsDTO } from "@easyshop/shared";
import { Card, StatCard, cx } from "@/components/ui";
import { eur, eur2, num } from "@/lib/format";

/** Arrivages groupés par mois, commune au réseau, à la fiche boutique et au mobile. Détail des produits au clic. */
export function ArrivalsMonths({ data, compact = false, showCity = false }: { data: ArrivalsDTO; compact?: boolean; showCity?: boolean }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  if (data.months.length === 0) {
    return <Card className="text-center text-[13px] text-muted">Aucun arrivage enregistré.</Card>;
  }
  return (
    <>
      {data.months.map((g) => {
        const units = g.items.reduce((s, a) => s + a.units, 0);
        return (
          <div key={g.key} className={cx("overflow-hidden border border-line bg-white", compact ? "rounded-card-sm" : "rounded-card")}>
            <div className={cx("flex items-baseline gap-3 bg-page", compact ? "px-[15px] py-[11px]" : "px-[22px] py-[14px]")}>
              <span className={cx("font-bold", compact ? "text-[12.5px]" : "text-[13.5px]")}>{g.label}</span>
              <span className="text-[11.5px] text-muted-2">
                {g.items.length} arrivage{g.items.length > 1 ? "s" : ""} · {num(units)} u.
              </span>
              <span className="flex-1" />
              <span className={cx("font-bold tabular", compact ? "text-[12px]" : "text-[13px]")}>{eur(g.total)}</span>
            </div>
            {g.items.map((a) => (
              <ArrivalRow
                key={a.id}
                a={a}
                compact={compact}
                showCity={showCity}
                open={expanded === a.id}
                onToggle={() => setExpanded((e) => (e === a.id ? null : a.id))}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

function ArrivalRow({
  a,
  compact,
  showCity,
  open,
  onToggle,
}: {
  a: ArrivalsDTO["months"][number]["items"][number];
  compact: boolean;
  showCity: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const d = a.date ? new Date(a.date) : null;
  const preview = [...a.lines].sort((x, y) => y.quantity - x.quantity).slice(0, 3).map((l) => l.name);
  const more = a.lines.length - preview.length;
  return (
    <div className="border-t border-line-row">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cx("flex w-full items-center gap-3 text-left hover:bg-[oklch(0.985_0.004_145)]", compact ? "px-[15px] py-[11px]" : "px-[22px] py-[13px]", open && "bg-[oklch(0.985_0.004_145)]")}
      >
        <div className="flex h-[42px] w-[42px] flex-shrink-0 flex-col items-center justify-center rounded-[10px] bg-accent-soft leading-none text-accent">
          <span className="font-display text-[16px] font-extrabold">{d ? d.getDate() : "?"}</span>
          <span className="mt-[2px] text-[9.5px] font-bold uppercase">{d ? d.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "") : "—"}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-bold">
            {a.supplier ?? "Sans fournisseur"}
            {showCity && <span className="ml-2 text-[12px] font-normal text-muted-2">· {a.city}</span>}
          </div>
          <div className="truncate text-[11.5px] text-muted-2">
            {a.lines.length === 0 ? "Détail des produits non disponible" : preview.join(", ") + (more > 0 ? ` +${more}` : "")}
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="text-[13px] font-bold tabular">{eur(a.amount)}</div>
          <div className="text-[11px] text-muted-2">
            {a.lines.length} produit{a.lines.length > 1 ? "s" : ""} · {num(a.units)} u.
          </div>
        </div>
        <span aria-hidden className={cx("flex-shrink-0 text-[11px] text-muted-2 transition-transform", open && "rotate-180")}>
          ▾
        </span>
      </button>
      {open && a.lines.length > 0 && (
        <div className={cx("bg-page pb-4 pt-2", compact ? "px-[15px]" : "px-[22px]")}>
          <div className="overflow-hidden rounded-card-sm border border-line bg-white">
            <div className="grid grid-cols-[1fr_52px_78px_84px] gap-2 border-b border-line-soft px-4 py-2 text-[10.5px] font-bold text-muted-2">
              <span>PRODUIT</span>
              <span className="text-right">QTÉ</span>
              <span className="text-right">COÛT U.</span>
              <span className="text-right">TOTAL</span>
            </div>
            {a.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_52px_78px_84px] items-center gap-2 border-b border-line-row px-4 py-[8px] text-[12px] last:border-b-0">
                <span className="min-w-0 truncate font-semibold" title={l.name}>
                  {l.name}
                </span>
                <span className="text-right tabular">{num(l.quantity)}</span>
                <span className="text-right tabular text-muted">{l.unitCost != null ? eur2(l.unitCost) : "—"}</span>
                <span className="text-right font-semibold tabular">{l.unitCost != null ? eur2(l.unitCost * l.quantity) : "—"}</span>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_52px_78px_84px] gap-2 bg-page px-4 py-2 text-[12px] font-bold">
              <span>Total</span>
              <span className="text-right tabular">{num(a.units)}</span>
              <span />
              <span className="text-right tabular">{eur2(a.amount)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Cartes récap « Valeur reçue / Unités ». */
export function ArrivalsSummary({ data, extended = false }: { data: ArrivalsDTO; extended?: boolean }) {
  return (
    <div className="flex flex-wrap gap-4">
      <StatCard size="sm" className="flex-1" label="Valeur reçue" value={eur(data.total)} />
      <StatCard size="sm" className="flex-1" label="Unités" value={num(data.units)} />
      {extended && <StatCard size="sm" className="flex-1" label="Fournisseurs" value={num(data.suppliers.length)} />}
      {extended && <StatCard size="sm" className="flex-1" label="Arrivage moyen" value={eur(data.average)} />}
    </div>
  );
}
